'use client'

/**
 * Шаг 1 импорта — загрузка файлов (Intelligence Terminal §Forms, §Buttons).
 */

import { useState, useCallback } from 'react'
import { Upload, FileXls, ArrowDown, ArrowUp, File as FileIcon } from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { useT, useFormat } from '@/lib/i18n'

type UploadMode = 'two-files' | 'single-file'

interface Props {
  onUpload: (importFile: File, exportFile: File | null) => void
  loading: boolean
}

export function StepUpload({ onUpload, loading }: Props) {
  const t = useT()
  const [mode, setMode] = useState<UploadMode>('single-file')
  const [importFile, setImportFile] = useState<File | null>(null)
  const [exportFile, setExportFile] = useState<File | null>(null)
  const [singleFile, setSingleFile] = useState<File | null>(null)

  const canStart = mode === 'single-file'
    ? !!singleFile
    : !!importFile && !!exportFile

  const handleStart = () => {
    if (mode === 'single-file' && singleFile) {
      onUpload(singleFile, null)
    } else if (importFile && exportFile) {
      onUpload(importFile, exportFile)
    }
  }

  return (
    <div className="space-y-4">
      {/* Mode selector */}
      <div className="flex gap-1 rounded-[4px] border border-[var(--border-subtle)] bg-[var(--bg-subtle)] p-1">
        <ModeButton
          active={mode === 'single-file'}
          onClick={() => setMode('single-file')}
        >
          {t('importWizard.upload.oneFile')}
        </ModeButton>
        <ModeButton
          active={mode === 'two-files'}
          onClick={() => setMode('two-files')}
        >
          {t('importWizard.upload.twoFiles')}
        </ModeButton>
      </div>

      {mode === 'single-file' ? (
        <>
          <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
            {t('importWizard.upload.oneFileHint')}
          </p>
          <FileDropZone
            label={t('importWizard.upload.singleLabel')}
            description={t('importWizard.upload.singleDescription')}
            icon={FileIcon}
            file={singleFile}
            onFile={setSingleFile}
            loading={loading}
          />
        </>
      ) : (
        <>
          <p className="text-[13px] leading-relaxed text-[var(--text-secondary)]">
            {t('importWizard.upload.twoFilesHint')}
          </p>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <FileDropZone
              label={t('importWizard.upload.importLabel')}
              description={t('importWizard.upload.importDescription')}
              icon={ArrowDown}
              file={importFile}
              onFile={setImportFile}
              loading={loading}
            />
            <FileDropZone
              label={t('importWizard.upload.exportLabel')}
              description={t('importWizard.upload.exportDescription')}
              icon={ArrowUp}
              file={exportFile}
              onFile={setExportFile}
              loading={loading}
            />
          </div>
        </>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleStart}
          disabled={!canStart || loading}
          className={cn(
            'inline-flex items-center gap-1.5 rounded-[4px] px-3 py-1.5 text-[12px] font-medium',
            'bg-accent-data text-fg-on-risk',
            'transition-colors duration-150',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-data)] focus-visible:ring-offset-2',
            'disabled:opacity-40 disabled:cursor-not-allowed',
          )}
        >
          {loading ? t('importWizard.upload.analyzing') : t('importWizard.upload.start')}
          <Upload size={12} />
        </button>
      </div>

      <p className="text-center font-mono text-[10px] tabular-nums text-[var(--text-tertiary)]">
        {t('importWizard.upload.formats')}
      </p>
    </div>
  )
}

function ModeButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex-1 rounded-[2px] px-3 py-2 text-[12px] font-medium transition-colors duration-150',
        active
          ? 'bg-canvas text-fg-primary'
          : 'bg-transparent text-fg-tertiary',
      )}
    >
      {children}
    </button>
  )
}

interface FileDropZoneProps {
  label: string
  description: string
  icon: React.ElementType
  file: File | null
  onFile: (f: File) => void
  loading: boolean
}

function FileDropZone({ label, description, icon: Icon, file, onFile, loading }: FileDropZoneProps) {
  const t = useT()
  const f = useFormat()
  const [dragActive, setDragActive] = useState(false)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragActive(false)
    const f = e.dataTransfer.files[0]
    if (f) onFile(f)
  }, [onFile])

  const handleSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) onFile(f)
  }, [onFile])

  return (
    <div
      className={cn(
        'rounded-[4px] border border-dashed p-6 text-center transition-colors duration-150',
      )}
      // eslint-disable-next-line no-restricted-syntax -- dynamic: цвет/фон drop-зоны по dragActive (color-mix), нет токен-утилиты
      style={{
        borderColor: dragActive ? 'var(--accent-data)' : 'var(--border-default)',
        background: dragActive
          ? 'color-mix(in oklch, var(--accent-data) 6%, transparent)'
          : file
            ? 'var(--bg-subtle)'
            : 'transparent',
      }}
      onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
      onDragLeave={() => setDragActive(false)}
      onDrop={handleDrop}
    >
      {loading ? (
        <div className="flex flex-col items-center gap-2">
          <div
            className="h-7 w-7 animate-spin rounded-full border-2 border-[var(--accent-data)] border-t-transparent"
            aria-hidden="true"
          />
          <p className="font-mono text-[11px] tabular-nums text-[var(--text-tertiary)]">
            {t('importWizard.upload.processing')}
          </p>
        </div>
      ) : file ? (
        <div className="flex flex-col items-center gap-2">
          <FileXls size={28} className="text-[var(--accent-data)]" />
          <p className="max-w-full truncate font-mono text-[12px] font-medium tabular-nums text-[var(--text-primary)]">
            {file.name}
          </p>
          <p className="font-mono text-[10px] tabular-nums text-[var(--text-tertiary)]">
            {t('importWizard.upload.sizeMb', { size: f.number(file.size / 1024 / 1024, { maximumFractionDigits: 1 }) })}
          </p>
          <label className="cursor-pointer">
            <input type="file" accept=".xls,.xlsx,.csv" className="hidden" onChange={handleSelect} />
            <span className="text-[11px] font-medium uppercase tracking-[0.06em] hover:underline text-accent-data">
              {t('importWizard.upload.replace')}
            </span>
          </label>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-2">
          <Icon size={28} className="opacity-60 text-[var(--text-tertiary)]" />
          <div>
            <p className="text-[12px] font-medium uppercase tracking-[0.06em] text-[var(--text-primary)]">
              {label}
            </p>
            <p className="mt-0.5 text-[11px] text-[var(--text-tertiary)]">{description}</p>
          </div>
          <label className="cursor-pointer">
            <input type="file" accept=".xls,.xlsx,.csv" className="hidden" onChange={handleSelect} />
            <span
              className={cn(
                'inline-flex items-center rounded-[4px] border border-[var(--border-default)] bg-transparent px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)]',
                'hover:border-[var(--border-strong)] hover:text-[var(--text-primary)]',
              )}
            >
              {t('importWizard.upload.choose')}
            </span>
          </label>
        </div>
      )}
    </div>
  )
}
